import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Pencil, Trash2, Share2, Printer, AlertCircle, BarChart3, PieChart as PieIcon, Layers, Trophy, SlidersHorizontal, Crosshair } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { ReportHeader } from '@/components/report/report-header';
import { ReportKpis } from '@/components/report/report-kpis';
import { ReportSegments } from '@/components/report/report-segments';
import { ReportSkuTable } from '@/components/report/report-sku-table';
import { ReportTopSkusChart } from '@/components/report/report-top-skus-chart';
import { ReportTrendMixed } from '@/components/reports/report-trend-mixed';
import { ReportCategoryDonut } from '@/components/reports/report-category-donut';
import { ReportBrandDonut } from '@/components/reports/report-brand-donut';
import { ReportTopSkusTable } from '@/components/reports/report-top-skus-table';
import { ReportSpecBreakdown } from '@/components/reports/report-spec-breakdown';
import { ReportMarketShare } from '@/components/reports/report-market-share';
import { ReportEditModal } from '@/components/reports/report-edit-modal';
import { ShareDialog } from '@/components/report/share-dialog';
import { cn } from '@/lib/utils';
import { formatVNDShort } from '@/lib/format';
import { deleteReport, getReport, resolveDisplayOptions, TEMPLATE_LABELS, brandLabel, isAllBrand } from '@/lib/api';
import type { Report, ReportStatus, ReportTrendPoint } from '@/lib/api';

const FOOTER_DATE = new Intl.DateTimeFormat('vi-VN', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'Asia/Ho_Chi_Minh',
});

const SNAPSHOT_DATETIME_FMT = new Intl.DateTimeFormat('vi-VN', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Asia/Ho_Chi_Minh',
});

const STATUS_STYLE: Record<ReportStatus, string> = {
  draft: 'bg-amber-500/[0.06] text-amber-800 border-amber-500/20',
  published: 'bg-teal-500/[0.06] text-teal-800 border-teal-500/25',
  archived: 'bg-muted/60 text-muted-foreground border-border',
};

const STATUS_LABEL: Record<ReportStatus, string> = {
  draft: 'Draft',
  published: 'Published',
  archived: 'Archived',
};

function formatMonthRange(months: string[]): string | undefined {
  if (months.length < 2) return months[0];
  const first = months[0];
  const last = months[months.length - 1];
  if (!first || !last) return undefined;
  const fmt = (m: string) => {
    const [y, mo] = m.split('-');
    return `${mo}/${y}`;
  };
  return `${fmt(first)} → ${fmt(last)}`;
}

export default function ReportDetailRoute() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getReport(id)
      .then((data) => {
        if (cancelled) return;
        setReport(data);
        document.title = `${data.title} — GearVN`;
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message);
        setReport(null);
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
      document.title = 'Monitor Dashboard — GearVN';
    };
  }, [id, reloadKey]);

  const snapshot = report?.snapshot ?? null;

  const focalSkus = useMemo(
    () =>
      (snapshot?.monitors ?? [])
        .filter((m) => m.is_focal === 1)
        .sort((a, b) => a.name.localeCompare(b.name, 'vi')),
    [snapshot]
  );

  const allSkus = useMemo(
    () =>
      [...(snapshot?.monitors ?? [])].sort((a, b) => {
        if (a.is_focal !== b.is_focal) return b.is_focal - a.is_focal;
        return a.name.localeCompare(b.name, 'vi');
      }),
    [snapshot]
  );

  const monthRange = useMemo(() => (snapshot ? formatMonthRange(snapshot.months) : undefined), [snapshot]);

  const handleEdit = useCallback(() => setEditOpen(true), []);

  const handleSaved = useCallback(() => {
    toast.success('Đã lưu thay đổi');
    setReloadKey((k) => k + 1);
  }, []);

  const handlePrint = useCallback(() => window.print(), []);

  const handleShare = useCallback(() => {
    if (!report) return;
    if (isAllBrand(report.brand)) {
      toast.warning('Báo cáo Toàn ngành không share được cho partner', {
        description: 'Aggregate cross-brand data — chỉ admin xem nội bộ.',
      });
      return;
    }
    if (report.status === 'draft') {
      toast.warning('Phải Publish trước khi share', {
        description: 'Đổi status sang Published trong Edit để chia sẻ.',
      });
      return;
    }
    setShareOpen(true);
  }, [report]);

  const confirmDelete = useCallback(async () => {
    if (!report) return;
    try {
      await deleteReport(report.id);
      toast.success(`Đã xóa báo cáo "${report.title}"`);
      navigate('/reports', { replace: true });
    } catch (err) {
      toast.error('Không xóa được báo cáo', { description: (err as Error).message });
    }
  }, [report, navigate]);

  const snapshotCreatedAt = report ? SNAPSHOT_DATETIME_FMT.format(new Date(report.created_at)) : '';
  const snapshotCreatedDate = report ? new Date(report.created_at) : new Date();
  // Resolve effective display options — override JSON if set, else template defaults.
  const displayOptions = report ? resolveDisplayOptions(report.template_type, report.display_options) : null;
  const templateMeta = report ? TEMPLATE_LABELS[report.template_type] : null;

  return (
    <div className="relative">
      <div className="print-watermark hidden">CONFIDENTIAL</div>

      {/* Action bar — mobile: icon-only + Share text; tablet+: full labels */}
      <div className="sticky top-0 z-20 flex items-center justify-between gap-1 border-b bg-background/80 px-3 py-2 backdrop-blur sm:gap-2 sm:px-6 sm:py-3 print:hidden">
        <Button asChild variant="ghost" size="sm" title="Danh sách báo cáo">
          <Link to="/reports">
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Danh sách báo cáo</span>
          </Link>
        </Button>
        <div className="flex items-center gap-1 sm:gap-2">
          <Button variant="outline" size="sm" onClick={handleEdit} disabled={loading || !report} title="Chỉnh sửa">
            <Pencil className="h-4 w-4" />
            <span className="hidden sm:inline">Chỉnh sửa</span>
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrint} disabled={loading || !report} title="In / Xuất PDF">
            <Printer className="h-4 w-4" />
            <span className="hidden sm:inline">In / PDF</span>
          </Button>
          <Button size="sm" onClick={handleShare} disabled={loading || !report}>
            <Share2 className="h-4 w-4" />
            <span className="sm:hidden">Share</span>
            <span className="hidden sm:inline">Chia sẻ</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDeleteOpen(true)}
            disabled={loading || !report}
            title="Xóa báo cáo"
            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
            <span className="hidden sm:inline">Xóa</span>
          </Button>
        </div>
      </div>

      <div className="report-typo-scale space-y-6 p-6">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Lỗi tải báo cáo</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading ? (
          <DetailSkeleton />
        ) : !report ? (
          <div className="rounded-lg border border-dashed bg-muted/30 p-12 text-center">
            <h2 className="text-lg font-semibold">Không tìm thấy báo cáo</h2>
            <Button asChild className="mt-4" size="sm">
              <Link to="/reports">
                <ArrowLeft className="h-4 w-4" />
                Về danh sách
              </Link>
            </Button>
          </div>
        ) : !snapshot ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Báo cáo chưa có snapshot</AlertTitle>
            <AlertDescription>
              Snapshot data trống — có thể do lỗi compute hoặc data import chưa đủ. Thử bấm "Chỉnh sửa" và cập nhật để tái tạo snapshot.
            </AlertDescription>
          </Alert>
        ) : (
          <>
            {/* Title block + status — admin only (hidden on print) */}
            <div className="space-y-2 print:hidden">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className={cn(STATUS_STYLE[report.status])}>
                  {STATUS_LABEL[report.status]}
                </Badge>
                <Badge variant="outline">{brandLabel(report.brand, { short: true })}</Badge>
                <Badge variant="outline">{report.period_label}</Badge>
                {templateMeta && (() => {
                  const Icon = templateMeta.icon;
                  return (
                    <Badge
                      variant="outline"
                      className="gap-1 border-primary/30 bg-primary/5 text-primary"
                      title={templateMeta.description}
                    >
                      <Icon className="h-3 w-3" />
                      {templateMeta.label}
                    </Badge>
                  );
                })()}
                <span className="basis-full text-[11px] text-muted-foreground sm:ml-auto sm:basis-auto">
                  Snapshot: {snapshotCreatedAt}
                </span>
              </div>
              <h1 className="text-xl font-bold tracking-tight">{report.title}</h1>
              {report.description && (
                <p className="text-sm text-muted-foreground">{report.description}</p>
              )}
            </div>

            {/* Intro text — visible on both admin view AND print/share for partner.
                Plain text với line breaks giữ nguyên qua whitespace-pre-line. */}
            {report.intro_text && (
              <div className="rounded-lg border-l-4 border-primary/40 bg-primary/5 px-4 py-3 text-sm leading-relaxed">
                <p className="whitespace-pre-line text-foreground">{report.intro_text}</p>
              </div>
            )}

            <ReportHeader brand={snapshot.brand} monthRange={monthRange} date={snapshotCreatedDate} />

            <ReportKpis
              totals={snapshot.totals}
              periodLabel={report.period_label}
              compare={
                snapshot.compare
                  ? { totals: snapshot.compare.totals, periodLabel: snapshot.compare.period.label }
                  : null
              }
              hideMarketShare={isAllBrand(report.brand)}
            />

            {/* Vị thế trong Ngành Monitor — comparison ASUS vs Toàn ngành.
                Skip cho all-brand (numbers redundant — toàn ngành is the market). */}
            {!isAllBrand(report.brand) && snapshot.market_total && snapshot.market_segments && (
              <section className="rounded-lg border bg-card p-4 shadow-sm print:rounded-md print:shadow-none print-no-break">
                <div className="mb-3">
                  <h2 className="flex items-center gap-2 text-sm font-semibold">
                    <Crosshair className="h-4 w-4 text-primary" />
                    Vị thế trong Ngành Monitor
                  </h2>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    {snapshot.brand} vs Toàn ngành Monitor — so sánh quy mô + thị phần per segment
                  </p>
                </div>
                <ReportMarketShare
                  brandName={snapshot.brand}
                  brandTotal={{ revenue_vnd: snapshot.totals.revenue_vnd, units_sold: snapshot.totals.units_sold }}
                  marketTotal={snapshot.market_total}
                  brandSegments={snapshot.segments}
                  marketSegments={snapshot.market_segments}
                />
              </section>
            )}

            <section className="rounded-lg border bg-card p-4 shadow-sm print:rounded-md print:shadow-none print-no-break">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <h2 className="flex items-center gap-2 text-sm font-semibold">
                    <BarChart3 className="h-4 w-4 text-primary" />
                    {snapshot.months.length === 1
                      ? `Top SKU theo doanh thu — ${report.period_label}`
                      : 'Số lượng & Doanh thu theo tháng'}
                  </h2>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    {snapshot.months.length === 1
                      ? `${snapshot.brand} · sắp xếp doanh thu giảm dần`
                      : `${snapshot.months.length} tháng · vùng = doanh thu, đường = số lượng`}
                  </p>
                </div>
              </div>
              {snapshot.months.length === 1 ? (
                <ReportTopSkusChart monitors={snapshot.monitors} />
              ) : (
                <div className="grid gap-4 lg:grid-cols-[1fr_260px]">
                  <ReportTrendMixed data={snapshot.trend} />
                  <MonthlyStatsPanel trend={snapshot.trend} />
                </div>
              )}
            </section>

            {/* Cơ cấu Segment + (optional) Cơ cấu theo Hãng — layout phụ thuộc context.
                Single-brand + single-donut → merge donut với segment cards (tận dụng width).
                Khác → standard 2-section layout. */}
            {(() => {
              const allBrand = isAllBrand(report.brand);
              const showMergedLayout = !allBrand && !displayOptions?.show_donut_units;
              return (
                <>
                  <div className={cn('grid gap-4', allBrand && 'lg:grid-cols-2')}>
                    <section className="rounded-lg border bg-card p-4 shadow-sm print:rounded-md print:shadow-none print-no-break">
                      <div className="mb-3">
                        <h2 className="flex items-center gap-2 text-sm font-semibold">
                          <PieIcon className="h-4 w-4 text-primary" />
                          Cơ cấu Segment
                        </h2>
                        <p className="mt-0.5 text-[11px] text-muted-foreground">
                          {snapshot.brand} · {showMergedLayout ? 'tỷ trọng + chi tiết theo phân khúc' : 'so sánh tỷ trọng theo Doanh thu vs Số lượng'}
                        </p>
                      </div>

                      {showMergedLayout ? (
                        <div className="grid gap-6 lg:grid-cols-[280px_1fr] lg:items-start">
                          <ReportCategoryDonut segments={snapshot.segments} metric="revenue" />
                          <ReportSegments segments={snapshot.segments} />
                        </div>
                      ) : (
                        <div className={cn(
                          'grid gap-6',
                          displayOptions?.show_donut_units && (allBrand ? 'xl:grid-cols-2' : 'md:grid-cols-2')
                        )}>
                          <div className="space-y-2">
                            <div className="text-center text-xs font-semibold text-muted-foreground">
                              Theo Doanh thu
                            </div>
                            <ReportCategoryDonut segments={snapshot.segments} metric="revenue" />
                          </div>
                          {displayOptions?.show_donut_units && (
                            <div className={cn(
                              'space-y-2 border-t pt-4',
                              allBrand
                                ? 'xl:border-l xl:border-t-0 xl:pl-6 xl:pt-0'
                                : 'md:border-l md:border-t-0 md:pl-6 md:pt-0'
                            )}>
                              <div className="text-center text-xs font-semibold text-muted-foreground">
                                Theo Số lượng bán
                              </div>
                              <ReportCategoryDonut segments={snapshot.segments} metric="units" />
                            </div>
                          )}
                        </div>
                      )}
                    </section>

                    {allBrand && (
                      <section className="rounded-lg border bg-card p-4 shadow-sm print:rounded-md print:shadow-none print-no-break">
                        <div className="mb-3">
                          <h2 className="flex items-center gap-2 text-sm font-semibold">
                            <Layers className="h-4 w-4 text-primary" />
                            Cơ cấu theo Hãng
                          </h2>
                          <p className="mt-0.5 text-[11px] text-muted-foreground">
                            Toàn ngành Monitor · so sánh tỷ trọng theo Doanh thu vs Số lượng
                          </p>
                        </div>
                        <div className={cn('grid gap-6', displayOptions?.show_donut_units && 'xl:grid-cols-2')}>
                          <div className="space-y-2">
                            <div className="text-center text-xs font-semibold text-muted-foreground">
                              Theo Doanh thu
                            </div>
                            <ReportBrandDonut monitors={snapshot.monitors} metric="revenue" idPrefix="brand-rev" />
                          </div>
                          {displayOptions?.show_donut_units && (
                            <div className="space-y-2 border-t pt-4 xl:border-l xl:border-t-0 xl:pl-6 xl:pt-0">
                              <div className="text-center text-xs font-semibold text-muted-foreground">
                                Theo Số lượng bán
                              </div>
                              <ReportBrandDonut monitors={snapshot.monitors} metric="units" idPrefix="brand-units" />
                            </div>
                          )}
                        </div>
                      </section>
                    )}
                  </div>

                  {!showMergedLayout && <ReportSegments segments={snapshot.segments} />}
                </>
              );
            })()}

            {/* Spec breakdown — partner thấy SKU phân theo kích thước/độ phân giải/tần số.
                Skip cho all-brand (đã có Cơ cấu theo Hãng). */}
            {!isAllBrand(report.brand) && (
              <section className="rounded-lg border bg-card p-4 shadow-sm print:rounded-md print:shadow-none print-no-break">
                <div className="mb-3">
                  <h2 className="flex items-center gap-2 text-sm font-semibold">
                    <SlidersHorizontal className="h-4 w-4 text-primary" />
                    Phân khúc theo Spec
                  </h2>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    Breakdown theo kích thước · độ phân giải · tần số quét — group tự động từ catalog
                  </p>
                </div>
                <ReportSpecBreakdown monitors={snapshot.monitors} />
              </section>
            )}

            {/* Top 15 SKU bán tốt nhất — sortable units / revenue / margin.
                Replace section "Sản phẩm Tập trung" cũ vì thông tin overlap. */}
            <section className="rounded-lg border bg-card p-4 shadow-sm print:rounded-md print:shadow-none">
              <div className="mb-3">
                <h2 className="flex items-center gap-2 text-sm font-semibold">
                  <Trophy className="h-4 w-4 text-primary" />
                  Top 15 SKU bán tốt nhất
                </h2>
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  Ranking theo số lượng / doanh thu / margin · top 3 highlight vàng
                </p>
              </div>
              <ReportTopSkusTable
                monitors={snapshot.monitors}
                maxRows={15}
                showInternalColumns={displayOptions?.show_internal_columns ?? true}
              />
            </section>

            <div className="print-break-before pt-2">
              <ReportSkuTable
                title="Sản phẩm Tập trung (Focal Point)"
                monitors={focalSkus}
                showInternalColumns={displayOptions?.show_internal_columns ?? true}
              />
            </div>

            {displayOptions?.show_all_skus && (
              <ReportSkuTable
                title="Toàn bộ SKU Active"
                monitors={allSkus}
                showInternalColumns={displayOptions?.show_internal_columns ?? true}
              />
            )}

            <footer className="flex items-center justify-between border-t pt-3 text-[10px] text-muted-foreground">
              <span>
                GearVN × {snapshot.brand} — Confidential | {FOOTER_DATE.format(snapshotCreatedDate)}
              </span>
              <span>
                {report.id} · {snapshot.total_skus} SKUs · {snapshot.focal_count} focal · {report.period_label}
              </span>
            </footer>
          </>
        )}
      </div>

      <ReportEditModal
        open={editOpen}
        onOpenChange={setEditOpen}
        report={report}
        onSaved={handleSaved}
      />

      {report && (
        <ShareDialog
          open={shareOpen}
          onOpenChange={setShareOpen}
          report={{ id: report.id, title: report.title, brand: report.brand }}
        />
      )}

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={report ? `Xóa báo cáo "${report.title}"?` : ''}
        description={
          <>
            Hành động <strong className="text-destructive">không thể hoàn tác</strong>. Các link share liên quan sẽ bị vô hiệu hóa ngay lập tức.
          </>
        }
        confirmText="Xóa báo cáo"
        cancelText="Hủy"
        variant="destructive"
        onConfirm={confirmDelete}
      />
    </div>
  );
}

function formatMonthLabel(month: string): string {
  const [year, m] = month.split('-');
  return `T${parseInt(m!, 10)}/${year!.slice(2)}`;
}

function MonthlyStatsPanel({ trend }: { trend: ReportTrendPoint[] }) {
  const totals = trend.reduce(
    (acc, p) => ({
      units: acc.units + p.units_sold,
      revenue: acc.revenue + p.revenue_vnd,
      margin: acc.margin + p.margin_vnd,
    }),
    { units: 0, revenue: 0, margin: 0 }
  );
  const avgMarginPct = totals.revenue > 0 ? (totals.margin / totals.revenue) * 100 : 0;

  return (
    <div className="flex flex-col rounded-md border bg-muted/30 p-3 text-xs">
      <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Chi tiết theo tháng
      </div>
      <div className="flex-1 space-y-1.5">
        {trend.map((p, i) => {
          const prev = i > 0 ? trend[i - 1] : null;
          const deltaRev = prev && prev.revenue_vnd > 0
            ? ((p.revenue_vnd - prev.revenue_vnd) / prev.revenue_vnd) * 100
            : null;
          return (
            <div key={p.month} className="rounded border bg-background p-2">
              <div className="flex items-center justify-between">
                <span className="font-semibold">{formatMonthLabel(p.month)}</span>
                {deltaRev !== null && (
                  <span
                    className={cn(
                      'text-[10px] font-medium tabular-nums',
                      Math.abs(deltaRev) < 0.5
                        ? 'text-muted-foreground'
                        : deltaRev > 0
                        ? 'text-emerald-700'
                        : 'text-destructive'
                    )}
                  >
                    {deltaRev > 0 ? '+' : ''}
                    {deltaRev.toFixed(1)}%
                  </span>
                )}
              </div>
              <div className="mt-1 grid grid-cols-2 gap-x-2 text-[11px]">
                <span className="text-muted-foreground">Units</span>
                <span className="text-right tabular-nums">{p.units_sold.toLocaleString('vi-VN')}</span>
                <span className="text-muted-foreground">Doanh thu</span>
                <span className="text-right tabular-nums font-medium">{formatVNDShort(p.revenue_vnd)}</span>
                <span className="text-muted-foreground">Margin %</span>
                <span className="text-right tabular-nums">{p.margin_pct.toFixed(1)}%</span>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-2 space-y-0.5 border-t pt-2 text-[11px]">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Tổng units</span>
          <span className="font-semibold tabular-nums">{totals.units.toLocaleString('vi-VN')}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Tổng doanh thu</span>
          <span className="font-semibold tabular-nums">{formatVNDShort(totals.revenue)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Margin TB</span>
          <span className="font-semibold tabular-nums">{avgMarginPct.toFixed(1)}%</span>
        </div>
      </div>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex gap-2">
          <Skeleton className="h-5 w-16" />
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-5 w-20" />
        </div>
        <Skeleton className="h-7 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
      </div>
      <div className="flex items-end justify-between border-b pb-4">
        <div className="space-y-2">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-7 w-72" />
          <Skeleton className="h-3 w-40" />
        </div>
        <Skeleton className="h-12 w-32" />
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24 rounded-lg" />
        ))}
      </div>
      <Skeleton className="h-64 rounded-lg" />
      <Skeleton className="h-56 rounded-lg" />
    </div>
  );
}
